import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { BackButtonComponent } from "@shared/components/back-button/back-button.component";
import { PulseSurveyService } from "@shared/services/legacy/pulse-survey.service";
import { switchMap } from "rxjs";
import { IconPipe } from "../../../shared/pipes/icon/icon.pipe";

@Component({
	selector: "app-pulse-survey-survey",
	imports: [TranslateModule, CommonModule, IconPipe, BackButtonComponent],
	templateUrl: "./pulse-survey-survey.component.html",
	styleUrl: "./pulse-survey-survey.component.scss",
})
export class PulseSurveySurveyComponent {
	readonly #route = inject(ActivatedRoute);
	readonly #router = inject(Router);
	readonly #pulseSurveyService = inject(PulseSurveyService);

	readonly #pulseSurveyId = signal(this.#route.snapshot.params.surveyId);

	public answers = signal<Record<string, string>>({});

	public pulseSurvey = toSignal(
		this.#pulseSurveyService.getPulseSurvey(this.#pulseSurveyId()),
	);

	public questions = computed(
		() => this.pulseSurvey()?.QuestionGroups?.[0]?.Questions ?? [],
	);

	public currentQuestionIndex = signal(0);

	public currentQuestion = computed(() => {
		const qs = this.questions();
		return this.currentQuestionIndex() < qs.length
			? qs[this.currentQuestionIndex()]
			: undefined;
	});

	public isConfirmationStep = computed(
		() => this.currentQuestionIndex() === this.questions().length,
	);

	public setAnswer(questionId: string, optionId: string) {
		this.answers.update((answers) => ({
			...answers,
			[questionId]: optionId,
		}));
		this.#pulseSurveyService.setPulseSurveyAnswers(
			this.#pulseSurveyId(),
			this.answers(),
		);
		// Auto-advance if not last question, else go to confirmation
		if (this.currentQuestionIndex() < this.questions().length - 1) {
			this.currentQuestionIndex.update((i) => i + 1);
		} else if (this.currentQuestionIndex() === this.questions().length - 1) {
			this.currentQuestionIndex.update((i) => i + 1); // Go to confirmation step
		}
	}

	public handleBack() {
		if (this.currentQuestionIndex() > 0) {
			this.currentQuestionIndex.update((i) => i - 1);
		}
	}

	public handleNext() {
		if (this.currentQuestionIndex() < this.questions().length - 1) {
			this.currentQuestionIndex.update((i) => i + 1);
		} else if (this.currentQuestionIndex() === this.questions().length - 1) {
			this.currentQuestionIndex.update((i) => i + 1); // Go to confirmation step
		}
	}

	public isAnsweredCurrent(): boolean {
		const q = this.currentQuestion();
		return !!q && !!this.answers()[q.Id];
	}

	public isFirstQuestion(): boolean {
		return this.currentQuestionIndex() === 0;
	}

	public handleFinish() {
		this.#pulseSurveyService
			.setPulseSurveyAnswers(this.#pulseSurveyId(), this.answers())
			.pipe(
				switchMap(() =>
					this.#pulseSurveyService.completePulseSurvey(this.#pulseSurveyId()),
				),
			)
			.subscribe(() => {
				this.#router.navigate([
					"pulse-survey",
					this.#pulseSurveyId(),
					"history",
				]);
			});
	}
}
